import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Target, Check } from "lucide-react";
import api from "../lib/api";

/**
 * The open goals board, surfaced inside the session at the Goals Review
 * section — so every goal gets looked in the eye rather than quietly skipped.
 * Progress can be nudged without leaving the room.
 */
export const GoalReviewPanel = ({ onMention }) => {
  const [goals, setGoals] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/goals")
      .then((r) => setGoals((r.data || []).filter((g) => g.status === "active")))
      .catch(() => {});
  }, []);

  const setProgress = async (goal, progress) => {
    setBusy(goal.id);
    try {
      const res = await api.patch(`/goals/${goal.id}`, { progress });
      setGoals((prev) => prev.map((g) => (g.id === goal.id ? res.data : g)).filter((g) => g.status === "active"));
      if (progress === 100) toast.success(`Goal closed · ${goal.title}`);
    } catch {
      toast.error("Could not update that goal");
    } finally {
      setBusy(null);
    }
  };

  if (goals.length === 0) return null;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="surface rounded-md p-4 mb-4" data-testid="goal-review-panel">
      <div className="flex items-center gap-2">
        <Target size={14} className="text-[#c89f45]" />
        <p className="label-eyebrow">On the board · face each one</p>
      </div>
      <ul className="mt-3 space-y-2">
        {goals.map((g) => {
          const overdue = g.target_date && g.target_date.slice(0, 10) < today;
          return (
            <li key={g.id} className="flex items-center gap-3" data-testid={`goal-review-${g.id}`}>
              <button
                type="button"
                onClick={() => onMention?.(g)}
                className="flex-1 min-w-0 text-left group"
                data-testid={`goal-review-mention-${g.id}`}
                title="Drop this goal into the notes"
              >
                <span className="text-sm text-[#ddd] group-hover:text-white truncate block">{g.title}</span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#777]">
                  {g.cadence} · {g.progress}%
                  {overdue && <span className="text-red-400"> · past due</span>}
                </span>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                {[25, 50, 75].map((p) => (
                  <button
                    key={p}
                    type="button"
                    disabled={busy === g.id}
                    onClick={() => setProgress(g, p)}
                    className={`text-[10px] font-mono px-1.5 py-0.5 rounded border transition-colors ${
                      g.progress === p
                        ? "border-[#c89f45] text-[#c89f45]"
                        : "border-[#2a2a2a] text-[#777] hover:text-white"
                    }`}
                    data-testid={`goal-review-set-${p}-${g.id}`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={busy === g.id}
                  onClick={() => setProgress(g, 100)}
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-[#2a2a2a] text-[#9bd3c4] hover:border-[#9bd3c4] transition-colors inline-flex items-center gap-1"
                  data-testid={`goal-review-done-${g.id}`}
                >
                  <Check size={10} /> Done
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
